/**
 * SyncedPhrase — Shared CapCut-style word-by-word reveal.
 *
 * Lights up each word at its true Whisper-aligned spoken moment when
 * `phrase.words[]` is populated, falls back to evenly distributing words
 * across [phrase.start, phrase.end] otherwise.
 *
 * Used by every Reset / Alignment / Ritual surface so the on-screen
 * cadence is identical site-wide (Golf, Aurum, Edge, Daily, etc.).
 *
 * Props:
 *   phrase        { start, end, text, words?: [{word,start,end}] }
 *   elapsed       Current audio time in seconds.
 *   nextStart     Optional — start of the next phrase, used as fallback end.
 *   className     Tailwind/style override for the wrapper <p>.
 *   style         Inline style override.
 *   wordSpacing   Tailwind-like utility for inter-word spacing (default mr-[0.28em]).
 *   testId        data-testid attribute for testing.
 */
import React from 'react';
import { motion } from 'framer-motion';

const buildPairs = (phrase, nextStart) => {
  const tokens = (phrase.text || '').split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];
  const words = Array.isArray(phrase.words) ? phrase.words : [];
  const t0 = phrase.start || 0;
  const tEnd = (Number.isFinite(phrase.end) ? phrase.end : null)
    || (Number.isFinite(nextStart) ? nextStart : null)
    || (t0 + tokens.length * 0.42);
  const span = Math.max(0.0001, tEnd - t0);
  return tokens.map((tok, i) => {
    if (words[i] && Number.isFinite(words[i].start)) {
      return { word: tok, start: words[i].start };
    }
    return { word: tok, start: t0 + (i / Math.max(tokens.length, 1)) * span };
  });
};

export const SyncedPhrase = ({
  phrase,
  elapsed = 0,
  nextStart = null,
  className = '',
  style = {},
  wordSpacing = 'mr-[0.28em]',
  testId,
  // Allow callers to override the per-word entrance animation.
  motionInitial = { opacity: 0, y: 6 },
  motionTransition = { duration: 0.45, ease: 'easeOut' },
}) => {
  if (!phrase || !phrase.text) return null;
  const pairs = buildPairs(phrase, nextStart);
  return (
    <p className={className} style={style} data-testid={testId}>
      {pairs.map((w, wi) => {
        const visible = elapsed >= w.start;
        return (
          <motion.span
            key={`${phrase.start}-${wi}`}
            initial={motionInitial}
            animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : (motionInitial.y || 6) }}
            transition={motionTransition}
            className={`inline-block ${wordSpacing}`}
          >
            {w.word}
          </motion.span>
        );
      })}
    </p>
  );
};

export default SyncedPhrase;
